import { QuestionnaireResponse } from "@medplum/fhirtypes";

import { createEntityAdapter, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { QuestionnaireResponseStatus } from "@/utils/enums";
import { ProceduresFormTypeWithId } from "@/zod-schemas/procedures";

import { RootState } from "..";

const proceduresAdapter = createEntityAdapter({
  selectId: (procedure: ProceduresFormTypeWithId) => procedure.id!,
});

interface ProceduresState {
  entities: ReturnType<typeof proceduresAdapter.getInitialState>["entities"];
  ids: ReturnType<typeof proceduresAdapter.getInitialState>["ids"];
  targetProcedure?: ProceduresFormTypeWithId | undefined;
}

const initialState: ProceduresState = {
  ...proceduresAdapter.getInitialState(),
  targetProcedure: undefined,
};

const reducers = {
  setProcedures: proceduresAdapter.setAll,
  setProcedure: proceduresAdapter.addOne,
  updateProcedure: proceduresAdapter.updateOne,
  deleteProcedure: proceduresAdapter.removeOne,
  setTargetProcedure: (
    state: ProceduresState,
    action: PayloadAction<ProceduresFormTypeWithId | undefined>,
  ) => {
    state.targetProcedure = action.payload;
  },
};

export const proceduresSlice = createSlice({
  name: "procedures",
  initialState,
  reducers,
});

export const { setProcedures, setProcedure, updateProcedure, deleteProcedure, setTargetProcedure } =
  proceduresSlice.actions;

// Selectors
export const selectAllProcedures = (state: RootState) => {
  const getPriority = (status?: QuestionnaireResponse["status"]): number => {
    if (!status) return 0;
    if (status === QuestionnaireResponseStatus.Stopped) return 2;
    return 1;
  };

  return Object.values(state.procedures.entities).sort(
    (a, b) => getPriority(a.status) - getPriority(b.status),
  );
};
export const selectAllActiveProcedures = (state: RootState) =>
  Object.values(state.procedures.entities).filter(
    (procedure) => procedure?.status !== QuestionnaireResponseStatus.Stopped,
  );
export const selectTargetProcedure = (state: RootState) => state.procedures.targetProcedure;

// Reducer
export default proceduresSlice.reducer;
